const CITY_HOTEL_PROGRESS_PHRASES = [
  "正在查找酒店信息",
  "正在匹配您的酒店偏好",
  "正在匹配附近酒店",
  "正在筛选优选酒店",
  "正在定位优选酒店",
  "正在加载酒店详情",
  "正在确认酒店位置",
  "正在匹配行程信息",
  "正在整理行程信息",
  "正在筛选合适房源",
  "正在比价筛选",
  "正在整理优选房源",
  "正在比对周边房源",
  "筛选核心酒店中",
  "正在规划行程路线",
  "筛选中",
  "正在比价",
  "比价中",
  "正在匹配高分民宿",
  "分析中",
  "筛选分析中",
];

const decodeHtmlEntities = (value = "") =>
  String(value)
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

const normalizeHotelAnchors = (text = "") =>
  String(text).replace(
    /<a\b([^>]*)>([\s\S]*?)<\/a>/gi,
    (_, attrs = "", inner = "") => {
      const title = decodeHtmlEntities(inner.replace(/<[^>]+>/g, "")).trim();
      const hrefMatch =
        attrs.match(/\shref\s*=\s*"([^"]+)"/i) ||
        attrs.match(/\shref\s*=\s*'([^']+)'/i);
      const href = hrefMatch?.[1] ? decodeHtmlEntities(hrefMatch[1]) : "";
      if (!title) return "";
      return href ? `[${title}](${href})` : title;
    },
  );

const tableRowToCells = (row = "") =>
  String(row)
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const formatTableRow = (cells = []) => `| ${cells.join(" | ")} |`;

const isDividerCells = (cells = []) =>
  cells.length >= 2 && cells.every((cell) => /^:?-{3,}:?$/.test(cell.trim()));

const normalizeCompactTableLine = (line = "") => {
  if (!/\|\s*\|/.test(line)) return line;

  const rawRows = String(line)
    .split(/\|\s*\|/)
    .map((row) => row.trim())
    .filter(Boolean);
  if (rawRows.length < 2) return line;

  const headerCells = tableRowToCells(rawRows[0]);
  const dividerCells = tableRowToCells(rawRows[1]);
  const columnCount = headerCells.length;
  if (
    columnCount < 2 ||
    dividerCells.length < columnCount ||
    !isDividerCells(dividerCells.slice(0, columnCount))
  ) {
    return line;
  }

  const tableRows = [
    formatTableRow(headerCells.slice(0, columnCount)),
    formatTableRow(dividerCells.slice(0, columnCount)),
  ];
  const trailingText = [];

  rawRows.slice(2).forEach((row) => {
    const cells = tableRowToCells(row);
    if (cells.length < columnCount) {
      trailingText.push(row.replace(/^\|/, "").trim());
      return;
    }

    tableRows.push(formatTableRow(cells.slice(0, columnCount)));
    const extra = cells.slice(columnCount).join("|").trim();
    if (extra) trailingText.push(extra);
  });

  return trailingText.length > 0
    ? `${tableRows.join("\n")}\n\n${trailingText.join(" ")}`
    : tableRows.join("\n");
};

const isTableLikeRow = (line = "") => {
  const cells = tableRowToCells(line);
  return cells.length >= 2 && String(line).includes("|");
};

const normalizeCompactTableContinuationLine = (line = "", columnCount = 0) => {
  if (!columnCount || !/\|\s*\|/.test(line)) return [line];

  const rows = String(line)
    .split(/\|\s*\|/)
    .map((row) => row.trim())
    .filter(Boolean);

  if (rows.length < 2) return [line];

  const output = [];
  const trailingText = [];
  rows.forEach((row) => {
    const cells = tableRowToCells(row);
    if (cells.length < columnCount) {
      trailingText.push(row.replace(/^\|/, "").trim());
      return;
    }

    output.push(formatTableRow(cells.slice(0, columnCount)));
    const extra = cells.slice(columnCount).join("|").trim();
    if (extra) trailingText.push(extra);
  });

  if (trailingText.length > 0) output.push("", trailingText.join(" "));
  return output.length > 0 ? output : [line];
};

const normalizeCompactTables = (text = "") => {
  const output = [];
  let tableColumnCount = 0;

  String(text)
    .split("\n")
    .forEach((line) => {
      const normalizedLine = normalizeCompactTableLine(line);
      const expandedLines =
        normalizedLine === line
          ? normalizeCompactTableContinuationLine(line, tableColumnCount)
          : normalizedLine.split("\n");

      expandedLines.forEach((expandedLine) => {
        output.push(expandedLine);

        const currentCells = tableRowToCells(expandedLine);
        const prevCells = tableRowToCells(output[output.length - 2] || "");
        if (
          isDividerCells(currentCells) &&
          prevCells.length >= 2 &&
          prevCells.length === currentCells.length
        ) {
          tableColumnCount = prevCells.length;
          return;
        }

        if (
          tableColumnCount &&
          expandedLine.trim() &&
          !isTableLikeRow(expandedLine)
        ) {
          tableColumnCount = 0;
        }
      });
    });

  return output.join("\n");
};

const splitTextBeforeTable = (line = "") => {
  const source = String(line);
  if (/^\s*\|/.test(source)) return source;

  const firstPipeIndex = source.indexOf("|");
  if (firstPipeIndex <= 0) return source;

  const prefix = source.slice(0, firstPipeIndex).trimEnd();
  const suffix = source.slice(firstPipeIndex);
  return prefix && isTableLikeRow(suffix) ? `${prefix}\n${suffix}` : source;
};

const repairCityHotelFragmentedMarkdown = (text = "") => {
  let repaired = String(text || "").replace(/\r\n/g, "\n");

  // "##" often arrives as a standalone chunk, then the title in next chunk.
  repaired = repaired.replace(/(^|\n)(#{1,6})\s*\n+\s*/g, "$1$2 ");
  repaired = repaired.replace(/(^|\n)\s*(#{1,6})(?=[^\s#\n])/g, "$1$2 ");

  // Bold markers are frequently split into isolated chunks.
  repaired = repaired.replace(/\*\*\s*\n+\s*(?=[^*\n])/g, "**");
  repaired = repaired.replace(/([^*\n])\s*\n+\s*\*\*/g, "$1**");

  // Split merged bullet items caused by tokenized "-" chunks.
  repaired = repaired.replace(/([，。；!?！？])\s*-\s*(?=\S)/g, "$1\n- ");
  repaired = repaired.replace(/(稳|省|足够)\s*-\s*(?=\S)/g, "$1\n- ");

  return repaired;
};

const stripOrphanStrongMarkers = (text = "") => {
  let cleaned = String(text || "");
  if (!cleaned) return cleaned;

  // Remove standalone "**" tokens produced by fragmented SSE chunks.
  cleaned = cleaned.replace(/(^|\s)\*\*(?=\s|$)/g, "$1");
  // Remove common stray closing markers before links/punctuation.
  cleaned = cleaned.replace(/(\S)\*\*(?=\[)/g, "$1 ");

  const markerCount = (cleaned.match(/\*\*/g) || []).length;
  // If marker count is still odd, prefer dropping markers over leaking raw "**".
  if (markerCount % 2 !== 0) {
    cleaned = cleaned.replace(/\*\*/g, "");
  }

  return cleaned;
};

const stripEmptyListItems = (text = "") =>
  String(text || "")
    .split("\n")
    .filter((line) => !/^\s*[-+*]\s*$/.test(line))
    .join("\n");

const normalizeMarkdownBlocks = (text = "") => {
  let normalized = String(text).replace(/\r\n/g, "\n");

  // Ensure heading markers always have a trailing space (supports glued forms like ###标题).
  normalized = normalized.replace(/(#{1,6})([^\s#\n])/g, "$1 $2");
  // Split horizontal rule and heading when they arrive glued (e.g. ---## 标题).
  normalized = normalized.replace(
    /(---+|\*\*\*+|___+)\s*(#{1,6}\s+)/g,
    "$1\n\n$2",
  );
  // Split heading blocks from inline text when they are glued in a single stream line.
  normalized = normalized.replace(/([^\n#])\s*(#{1,6}\s+)/g, "$1\n\n$2");
  normalized = normalized.replace(/(#{1,6}\s*[^\n]+?)(?=\s+-\s*\*\*)/g, "$1\n");
  normalized = normalized.replace(/(#{1,6}\s*[^\n]+?)(?=\s+[-+*]\s+)/g, "$1\n");

  normalized = normalized.replace(/([^\n])\s*(---+|\*\*\*+|___+)\s*(?=\n|#{1,6}\s|\*\*|$)/g, "$1\n\n$2\n\n");
  normalized = normalized.replace(/(^|\n)(---+|\*\*\*+|___+)([^\n])/g, "$1$2\n\n$3");

  normalized = normalized.replace(
    /(^|[^\n-])(-\s*\*\*[^*\n]+?\*\*)/g,
    "$1\n$2",
  );
  normalized = normalized.replace(/(^|\n)([-+*])(?=\*\*)/g, "$1$2 ");
  // Only split inline list/number markers when separated by spaces, not by newlines.
  normalized = normalized.replace(/([^\n])([-+*][ \t]+(?=\S))/g, "$1\n$2");
  normalized = normalized.replace(/([^\n])(\d+[.)][ \t]+(?=\S))/g, "$1\n$2");

  normalized = normalized.split("\n").map(splitTextBeforeTable).join("\n");
  normalized = normalized.replace(/\|\s*(?=[^\n|])/g, "| ");
  normalized = normalizeCompactTables(normalized);

  normalized = normalized.replace(
    /(#{2,6}\s*[^\n#|]+?)评分\s*([0-9.]+)\s*\|\s*最低价\s*(?:\*\*)?([0-9.]+元)(?:\*\*)?/g,
    "$1\n评分 $2 | 最低价 $3\n",
  );
  normalized = normalized.replace(
    /(#{2,6}\s*[^\n#|]+?)评分\s*([0-9.]+)\s*\|\s*最低价\s*约\s*(?:\*\*)?([0-9.]+元)(?:\*\*)?/g,
    "$1\n评分 $2 | 最低价约 $3\n",
  );

  normalized = normalized.replace(/(\]\([^)]+\))(?=\d+元)/g, "$1 ");
  normalized = normalized.replace(/([^\n])(\*\*缺点\*\*[:：])/g, "$1\n$2");
  normalized = normalized.replace(/[ \t]+\n/g, "\n");
  normalized = normalized.replace(/\n{3,}/g, "\n\n");

  return normalized.trim();
};

export const normalizeMarkdownForRender = (text = "", options = {}) => {
  const source = options?.source || "general";
  if (!text) return "";

  let normalized = String(text).replace(/\r\n/g, "\n");

  if (source === "city_hotel_sse") {
    normalized = normalizeHotelAnchors(normalized);
    normalized = normalized.replace(/<[^>]+>/g, "");
    normalized = repairCityHotelFragmentedMarkdown(normalized);
    CITY_HOTEL_PROGRESS_PHRASES.forEach((phrase) => {
      normalized = normalized.split(phrase).join("");
    });
    // Fix malformed bold segments from fragmented SSE chunks:
    // remove accidental spaces right inside opening/closing ** delimiters.
    normalized = normalized.replace(/\*\*\s+([^*\n])/g, "**$1");
    normalized = normalized.replace(/([^*\n])\s+\*\*/g, "$1**");
    normalized = normalized
      .split("\n")
      .filter((line) => !/^\s*####\s+/.test(line))
      .join("\n");
    // Promote hotel-title strong segments to list items for better scanability.
    normalized = normalized.replace(
      /([^\n])(\*\*[^*\n]{2,60}(?:酒店|宾馆|民宿|公寓)[^*\n]{0,40}\*\*)/g,
      "$1\n\n- $2",
    );
    normalized = normalized.replace(
      /([^\n])(\*\*[⭐💰🌟🎯🛖🌸]\s*[^*\n]+?\*\*)/g,
      "$1\n\n- $2",
    );
    normalized = stripOrphanStrongMarkers(normalized);
    normalized = normalized.replace(/\n?\s*0\s*$/, "");
  }

  const rendered = normalizeMarkdownBlocks(normalized);
  return source === "city_hotel_sse" ? stripEmptyListItems(rendered) : rendered;
};
